import * as React from "react";
import { Download, Trash2, UserX } from "lucide-react";
import type { SelectionAction } from "../../../ui/components/Table";
import * as adminUsersApi from "../../lib/api/admin-users";
import type { AdminUserListItem } from "../../lib/api/admin-users";

function escapeCsv(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function downloadUsersCsv(rows: AdminUserListItem[]): void {
  const headers = [
    "id",
    "email",
    "firstName",
    "lastName",
    "phone",
    "emailVerified",
    "phoneVerified",
    "signIn",
    "disabled",
  ];
  const lines = rows.map((u) =>
    [
      u.id,
      u.email,
      u.firstName,
      u.lastName,
      u.phone ?? "",
      u.isEmailVerified ? "yes" : "no",
      u.isPhoneVerified ? "yes" : "no",
      u.googleId ? "google" : "email",
      u.deletedAt ? "yes" : "no",
    ]
      .map((v) => escapeCsv(String(v)))
      .join(","),
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface BuildUserSelectionActionsOptions {
  accessToken: string;
  canDelete: boolean;
  canHardDelete: boolean;
  confirm: (title: string, body: string, confirmLabel?: string) => Promise<boolean>;
  /** Rows from the latest list fetch (current page); used for export. */
  getVisibleRows: () => AdminUserListItem[];
  onMutationComplete?: () => void;
}

export function buildUserSelectionActions(
  opts: BuildUserSelectionActionsOptions,
): SelectionAction[] {
  const { accessToken, canDelete, canHardDelete, confirm, getVisibleRows, onMutationComplete } = opts;
  const actions: SelectionAction[] = [];

  actions.push({
    icon: <Download size={14} />,
    label: "Export CSV",
    onClick: (ids) => {
      const visible = getVisibleRows();
      const selected = visible.filter((r) => ids.includes(r.id));
      if (selected.length === 0) {
        window.alert(
          "No exported rows on this page. Change page or clear selection and try again.",
        );
        return;
      }
      if (selected.length < ids.length) {
        const ok = window.confirm(
          `Export ${selected.length} of ${ids.length} selected users (only rows on the current page are available). Continue?`,
        );
        if (!ok) return;
      }
      downloadUsersCsv(selected);
    },
  });

  if (canDelete) {
    actions.push({
      icon: <UserX size={14} />,
      label: "Disable",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Disable ${names[0]}?`
          : `Disable ${ids.length} user${ids.length !== 1 ? 's' : ''}?`
        const ok = await confirm(
          title,
          "They will be signed out and unable to sign in until restored.",
          "Disable",
        );
        if (!ok) return;
        try {
          await Promise.all(
            ids.map((id) => adminUsersApi.disableUser(accessToken, id)),
          );
          onMutationComplete?.();
        } catch (err) {
          const message = err instanceof Error ? err.message : "Disable failed";
          window.alert(message);
        }
      },
    });
  }

  if (canHardDelete) {
    actions.push({
      icon: <Trash2 size={14} />,
      label: "Delete permanently",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Delete ${names[0]} permanently?`
          : `Delete ${ids.length} user${ids.length !== 1 ? 's' : ''} permanently?`
        const ok = await confirm(
          title,
          "This will permanently remove the selected users and all their data. This cannot be undone.",
          "Delete permanently",
        );
        if (!ok) return;
        try {
          await Promise.all(
            ids.map((id) => adminUsersApi.deleteUserPermanently(accessToken, id)),
          );
          onMutationComplete?.();
        } catch (err) {
          window.alert(err instanceof Error ? err.message : "Delete failed");
        }
      },
    });
  }

  return actions;
}
